import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Crown, Trash2, X, Loader2, Pencil, Sparkles, Plus, Minus, Save } from "lucide-react";
import client, { formatApiError } from "@/lib/api";

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [confirmDel, setConfirmDel] = useState(null);
  const [premModal, setPremModal] = useState(null);
  const [until, setUntil] = useState("");
  const [busy, setBusy] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", learning_lang: "", level: "" });
  const [bal, setBal] = useState(null);
  const [grantAmt, setGrantAmt] = useState("");

  const load = () => client.get("/admin/users").then(({ data }) => setUsers(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const list = users.filter((u) => (u.email + (u.name || "")).toLowerCase().includes(q.toLowerCase()));

  const openEdit = async (u) => {
    setEditUser(u);
    setForm({ name: u.name || "", email: u.email || "", learning_lang: u.learning_lang || "", level: u.level || "" });
    setBal(null); setGrantAmt("");
    try { const { data } = await client.get(`/admin/users/${u.id}/credits`); setBal(data); } catch { /* ignore */ }
  };

  const saveEdit = async () => {
    setBusy(true);
    try {
      await client.put(`/admin/users/${editUser.id}`, form);
      toast.success("Korisnik ažuriran");
      await load();
      setEditUser(null);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const grant = async (amount) => {
    if (!amount) return;
    setBusy(true);
    try {
      const { data } = await client.post(`/admin/users/${editUser.id}/credits`, { amount, note: "admin panel" });
      setBal((b) => ({ ...(b || {}), total_remaining: data.balance, included_remaining: data.included_remaining, purchased: data.purchased }));
      setGrantAmt("");
      toast.success(amount > 0 ? `Dodano ${amount} kredita` : `Oduzeto ${-amount} kredita`);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const doDelete = async () => {
    setBusy(true);
    try { await client.delete(`/admin/users/${confirmDel.id}`); toast.success("Korisnik obrisan"); setConfirmDel(null); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const setPremium = async (grant) => {
    setBusy(true);
    try {
      await client.post(`/admin/users/${premModal.id}/premium`, { premium: grant, until: grant && until ? new Date(until).toISOString() : null });
      toast.success(grant ? "Premium uključen" : "Premium isključen");
      setPremModal(null); setUntil(""); await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div><h1 className="font-heading text-2xl font-extrabold">Users</h1><p className="text-slate-500">{users.length} registered</p></div>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search users…" data-testid="user-search" className="bg-white border border-slate-200 rounded-xl px-4 py-2 outline-none w-full sm:w-64" />
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl overflow-x-auto">
        <table className="w-full min-w-[820px] text-sm" data-testid="admin-users-table">
          <thead className="bg-slate-50 text-slate-400 text-left"><tr>
            <th className="px-5 py-3 font-semibold">Name</th><th className="px-5 py-3 font-semibold">Email</th>
            <th className="px-5 py-3 font-semibold">Role</th><th className="px-5 py-3 font-semibold">Plan</th>
            <th className="px-5 py-3 font-semibold">Learning</th><th className="px-5 py-3 font-semibold">Level</th>
            <th className="px-5 py-3 font-semibold">Credits</th>
            <th className="px-5 py-3 font-semibold">Minutes</th><th className="px-5 py-3 font-semibold text-right">Actions</th>
          </tr></thead>
          <tbody>
            {list.map((u) => (
              <tr key={u.id} className="border-t border-slate-100" data-testid={`user-row-${u.id}`}>
                <td className="px-5 py-3 font-medium">{u.name || "—"}</td>
                <td className="px-5 py-3 text-slate-500">{u.email}</td>
                <td className="px-5 py-3"><span className={`text-xs font-bold px-2 py-1 rounded-full ${u.role === "admin" ? "bg-purple-100 text-purple-600" : "bg-slate-100 text-slate-500"}`}>{u.role}</span></td>
                <td className="px-5 py-3" data-testid={`user-plan-${u.id}`}>
                  <span className={`text-xs font-bold px-2 py-1 rounded-full ${u.plan === "Premium" ? "bg-amber-100 text-amber-600" : "bg-slate-100 text-slate-500"}`}>
                    {u.plan || "Free"}{u.plan === "Premium" && u.cancel_at_period_end ? " ⏳" : ""}
                  </span>
                </td>
                <td className="px-5 py-3 uppercase">{u.learning_lang || "—"}</td>
                <td className="px-5 py-3">{u.level || "—"}</td>
                <td className="px-5 py-3" data-testid={`user-credits-${u.id}`}>
                  <span className="inline-flex items-center gap-1 text-xs font-bold text-blue-700 bg-blue-50 px-2 py-1 rounded-full">
                    <Sparkles size={12} /> {u.credits_total != null ? u.credits_total.toLocaleString("de-CH") : "—"}
                  </span>
                </td>
                <td className="px-5 py-3 font-semibold">{u.speaking_minutes}</td>
                <td className="px-5 py-3">
                  <div className="flex items-center justify-end gap-1.5">
                    <button onClick={() => openEdit(u)} data-testid={`edit-btn-${u.id}`}
                      className="p-2 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors" title="Edit user & credits">
                      <Pencil size={16} />
                    </button>
                    <button onClick={() => { setPremModal(u); setUntil(""); }} data-testid={`premium-btn-${u.id}`}
                      className={`p-2 rounded-lg transition-colors ${u.plan === "Premium" ? "bg-amber-100 text-amber-600 hover:bg-amber-200" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`} title="Manage Premium">
                      <Crown size={16} />
                    </button>
                    {u.role !== "admin" && (
                      <button onClick={() => setConfirmDel(u)} data-testid={`delete-btn-${u.id}`} className="p-2 rounded-lg bg-red-50 text-red-500 hover:bg-red-100 transition-colors" title="Delete user">
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Edit user + credits modal */}
      {editUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setEditUser(null)} data-testid="edit-user-modal">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-lg font-extrabold flex items-center gap-2"><Pencil size={18} className="text-blue-600" /> Uredi korisnika</h3>
              <button onClick={() => setEditUser(null)} className="text-slate-400"><X size={18} /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-slate-500">Ime</label>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="edit-name" className="w-full border border-slate-200 rounded-xl px-3 py-2 mt-1 outline-none focus:border-blue-500" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500">Email</label>
                <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="edit-email" className="w-full border border-slate-200 rounded-xl px-3 py-2 mt-1 outline-none focus:border-blue-500" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-slate-500">Jezik učenja</label>
                  <input value={form.learning_lang} onChange={(e) => setForm({ ...form, learning_lang: e.target.value })} placeholder="de" data-testid="edit-learning" className="w-full border border-slate-200 rounded-xl px-3 py-2 mt-1 outline-none focus:border-blue-500 uppercase" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-500">Nivo</label>
                  <select value={form.level} onChange={(e) => setForm({ ...form, level: e.target.value })} data-testid="edit-level" className="w-full border border-slate-200 rounded-xl px-3 py-2 mt-1 outline-none focus:border-blue-500 bg-white">
                    <option value="">—</option>
                    {["A1", "A2", "B1", "B2", "C1", "C2"].map((l) => <option key={l} value={l}>{l}</option>)}
                  </select>
                </div>
              </div>
              <button onClick={saveEdit} disabled={busy} data-testid="edit-save" className="w-full mel-gradient text-white font-semibold py-2.5 rounded-xl disabled:opacity-60 flex items-center justify-center gap-2">
                {busy ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />} Sačuvaj izmjene
              </button>
            </div>

            <div className="mt-5 pt-5 border-t border-slate-100">
              <div className="flex items-center justify-between mb-3">
                <span className="font-heading font-bold flex items-center gap-2"><Sparkles size={16} className="text-blue-600" /> MELLETIC Krediti</span>
                <span className="text-sm font-extrabold text-blue-700" data-testid="edit-credit-balance">
                  {bal ? `${(bal.total_remaining ?? 0).toLocaleString("de-CH")}` : "…"}
                  {bal && <span className="text-slate-400 font-medium"> ({bal.plan})</span>}
                </span>
              </div>
              {bal && (
                <p className="text-xs text-slate-400 mb-3">Uključeni: {bal.included_remaining?.toLocaleString("de-CH")} · Kupljeni: {bal.purchased?.toLocaleString("de-CH")}</p>
              )}
              <div className="flex flex-wrap gap-2 mb-3">
                {[100, 500, 1500, 3000].map((n) => (
                  <button key={n} onClick={() => grant(n)} disabled={busy} data-testid={`grant-${n}`}
                    className="flex items-center gap-1 text-sm font-bold bg-blue-50 text-blue-700 px-3 py-1.5 rounded-lg hover:bg-blue-100 disabled:opacity-60">
                    <Plus size={13} /> {n}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <input type="number" value={grantAmt} onChange={(e) => setGrantAmt(e.target.value)} placeholder="Iznos" data-testid="grant-amount" className="flex-1 border border-slate-200 rounded-xl px-3 py-2 outline-none focus:border-blue-500" />
                <button onClick={() => grant(Math.abs(parseInt(grantAmt || "0", 10)))} disabled={busy || !grantAmt} data-testid="grant-add" className="flex items-center gap-1 bg-emerald-500 text-white font-semibold px-3 py-2 rounded-xl disabled:opacity-60"><Plus size={15} /> Dodaj</button>
                <button onClick={() => grant(-Math.abs(parseInt(grantAmt || "0", 10)))} disabled={busy || !grantAmt} data-testid="grant-remove" className="flex items-center gap-1 bg-slate-100 text-slate-600 font-semibold px-3 py-2 rounded-xl disabled:opacity-60"><Minus size={15} /> Skini</button>
              </div>
              <p className="text-[11px] text-slate-400 mt-2">Dodani krediti idu u "kupljene" (ne ističu), isto kao kupovina paketa.</p>
            </div>
          </div>
        </div>
      )}

      {/* Premium modal */}
      {premModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setPremModal(null)} data-testid="premium-modal">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-heading text-lg font-extrabold flex items-center gap-2"><Crown size={18} className="text-amber-500" /> Premium</h3>
              <button onClick={() => setPremModal(null)} className="text-slate-400"><X size={18} /></button>
            </div>
            <p className="text-slate-500 text-sm mb-4">{premModal.email} — trenutno <b>{premModal.plan}</b></p>
            <label className="block text-xs font-semibold text-slate-500 mb-1">Premium važi do (opcionalno)</label>
            <input type="date" value={until} onChange={(e) => setUntil(e.target.value)} data-testid="premium-until" className="w-full border border-slate-200 rounded-xl px-3 py-2 mb-4 outline-none" />
            <div className="flex gap-2">
              <button onClick={() => setPremium(true)} disabled={busy} data-testid="grant-premium-confirm" className="flex-1 mel-gradient text-white font-semibold py-2.5 rounded-xl disabled:opacity-60">
                {busy ? <Loader2 className="animate-spin mx-auto" size={16} /> : "Uključi Premium"}
              </button>
              <button onClick={() => setPremium(false)} disabled={busy} data-testid="revoke-premium-confirm" className="flex-1 bg-slate-100 text-slate-600 font-semibold py-2.5 rounded-xl disabled:opacity-60">Isključi</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {confirmDel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setConfirmDel(null)} data-testid="delete-modal">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading text-lg font-extrabold text-red-600">Obriši korisnika?</h3>
            <p className="text-slate-500 text-sm mt-1 mb-5">{confirmDel.email} i svi njegovi podaci bit će trajno obrisani. Ovo se ne može poništiti.</p>
            <div className="flex gap-2">
              <button onClick={doDelete} disabled={busy} data-testid="delete-confirm" className="flex-1 bg-red-500 text-white font-semibold py-2.5 rounded-xl disabled:opacity-60">
                {busy ? <Loader2 className="animate-spin mx-auto" size={16} /> : "Obriši"}
              </button>
              <button onClick={() => setConfirmDel(null)} className="flex-1 bg-slate-100 text-slate-600 font-semibold py-2.5 rounded-xl">Odustani</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
